import React, { useState } from "react";
import cs from "classnames";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import styles from "./sections.module.scss";

export type MetadataKeyword = {
  term: string;
  popularity?: number | null;
  rank?: number | null;
};

export type MetadataAuditProps = {
  /** The metadata value under audit (title, subtitle, description). */
  value?: string | null;
  /** Store limit for this field, in characters. */
  limit: number;
  /** Ranked keywords the value actually contains. */
  keywords?: MetadataKeyword[];
  /** One-line verdict rendered above the bar. */
  verdict?: string;
  /**
   * Keyword measurement is still running. Without this the block would report
   * "0 ranked keywords", which reads as a finding rather than "not measured".
   */
  keywordsPending?: boolean;
  emptyText?: string;
  className?: string;
};

// How the field's keywords are doing, the way every ASO report cuts it.
const PERFORMANCE_TIERS = [
  { label: "Top 3", max: 3 },
  { label: "Top 10", max: 10 },
  { label: "Top 50", max: 50 },
];

/**
 * Character budget + the ranked keywords a metadata field carries.
 * Shared by the App Name, Subtitle and Description sections — the three differ
 * only in their limit and in what counts as "used".
 */
export const MetadataAudit: React.FC<MetadataAuditProps> = ({
  value,
  limit,
  keywords = [],
  verdict,
  keywordsPending = false,
  emptyText = "No data yet",
  className,
}) => {
  if (!value) return <p className={styles.empty}>{emptyText}</p>;

  const used = value.length;
  const percent = Math.min(100, Math.round((used / limit) * 100));
  // Under half the budget is wasted space; over the limit the store truncates.
  const tone = used > limit ? "over" : percent < 50 ? "low" : "ok";

  return (
    <div className={cs(styles.audit, className)}>
      <p className={styles.value}>{value}</p>

      <div className={styles.meter}>
        <div className={styles.meterHead}>
          <span className={styles.meterLabel}>
            {used} / {limit} characters
          </span>
          {verdict && (
            <span className={cs(styles.verdict, styles[`verdict-${tone}`])}>
              {verdict}
            </span>
          )}
        </div>
        <span className={styles.meterTrack}>
          <span
            className={cs(styles.meterBar, styles[`meterBar-${tone}`])}
            style={{ width: `${percent}%` }}
          />
        </span>
      </div>

      <div className={styles.keywords}>
        <span className={styles.keywordsLabel}>
          {keywordsPending
            ? "Measuring the app's keywords…"
            : `Ranked keywords in this field: ${keywords.length}`}
        </span>

        {!keywordsPending && keywords.length > 0 && (
          <div className={styles.performance}>
            {PERFORMANCE_TIERS.map((tier) => (
              <span className={styles.tier} key={tier.label}>
                <span className={styles.tierLabel}>{tier.label}</span>
                <span className={styles.tierValue}>
                  {
                    keywords.filter((k) => k.rank != null && k.rank <= tier.max)
                      .length
                  }
                </span>
              </span>
            ))}
          </div>
        )}
        {keywords.length > 0 && (
          <ul className={styles.keywordList}>
            {keywords.map((keyword) => (
              <li className={styles.keyword} key={keyword.term}>
                <span className={styles.keywordTerm}>{keyword.term}</span>
                {keyword.rank != null && (
                  <span className={styles.keywordMeta}>#{keyword.rank}</span>
                )}
                {keyword.popularity != null && (
                  <span className={styles.keywordMeta}>
                    vol {keyword.popularity}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export type MetadataFieldProps = {
  /** The field's text as the store shows it. */
  value?: string | null;
  /** Ranked keywords the text actually contains. */
  keywords?: MetadataKeyword[];
  /** Keyword measurement is still running — see MetadataAudit. */
  keywordsPending?: boolean;
  emptyText?: string;
  className?: string;
};

/**
 * A metadata field as the report shows it (Figma "Description"): the text in
 * full, then the ranked keywords it carries as chips — term, position, demand.
 * The character budget lives in the section's badge, not here.
 */
export const MetadataField: React.FC<MetadataFieldProps> = ({
  value,
  keywords = [],
  keywordsPending = false,
  emptyText = "No data yet",
  className,
}) => {
  if (!value) return <p className={styles.empty}>{emptyText}</p>;

  return (
    <div className={cs(styles.field, className)}>
      <p className={styles.fieldText}>{value}</p>

      <div className={styles.fieldKeywords}>
        <span className={styles.fieldKeywordsLabel}>
          {keywordsPending
            ? "Measuring the app's keywords…"
            : `Ranked keywords in this field: ${keywords.length}`}
        </span>
        {keywords.length > 0 && (
          <ul className={styles.chips}>
            {keywords.map((keyword) => (
              <li className={styles.chip} key={keyword.term}>
                <span className={styles.chipTerm}>{keyword.term}</span>
                {keyword.rank != null && (
                  <>
                    <span className={styles.chipDot}>•</span>
                    <span className={styles.chipMeta}>#{keyword.rank}</span>
                  </>
                )}
                {keyword.popularity != null && (
                  <>
                    {keyword.rank != null && (
                      <span className={styles.chipDot}>•</span>
                    )}
                    <span className={styles.chipMeta}>
                      vol. {keyword.popularity}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export type KeywordsBucket = {
  label: string;
  count: number;
  /** Drawn in the brand colour — the positions that bring traffic. */
  strong?: boolean;
};

export type KeywordsDistributionProps = {
  /** Position buckets, best first. */
  buckets: KeywordsBucket[];
  /** What each bar is a share of — every keyword measured. */
  total: number;
  emptyText?: string;
  className?: string;
};

/**
 * Where the app's keywords sit in the results — the core of the report.
 * A bar per position bucket as a share of all measured keywords; the top
 * buckets are drawn strong, the long tail soft.
 */
export const KeywordsDistribution: React.FC<KeywordsDistributionProps> = ({
  buckets,
  total,
  emptyText = "No data yet",
  className,
}) => {
  if (!total) return <p className={styles.empty}>{emptyText}</p>;

  return (
    <div className={cs(styles.distribution, className)}>
      {buckets.map((bucket) => (
        <div className={styles.bucket} key={bucket.label}>
          <span className={styles.bucketLabel}>{bucket.label}</span>
          <span className={styles.bucketTrack}>
            <span
              className={cs(styles.bucketBar, {
                [styles.bucketBarStrong]: bucket.strong,
              })}
              style={{
                width: `${Math.min(100, Math.round((bucket.count / total) * 100))}%`,
              }}
            />
          </span>
          <span className={styles.bucketCount}>{bucket.count}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * The banner Apple shows above the app's App Store page (Figma "Banner"),
 * at its own proportions. A broken image falls back to the empty text
 * instead of leaving a blank frame.
 */
export const BannerPreview: React.FC<{
  url?: string | null;
  emptyText?: string;
  className?: string;
}> = ({ url, emptyText = "No data yet", className }) => {
  const [failed, setFailed] = useState<string | null>(null);

  if (!url || failed === url) {
    return <p className={styles.empty}>{emptyText}</p>;
  }

  return (
    <img
      className={cs(styles.banner, className)}
      src={url}
      alt=""
      onError={() => setFailed(url)}
    />
  );
};

export type InfoGridRow = { label: string; value?: React.ReactNode };

/** Label → value rows used by the App Info and Versions sections. */
export const InfoGrid: React.FC<{
  rows: InfoGridRow[];
  emptyText?: string;
  className?: string;
}> = ({ rows, emptyText = "No data yet", className }) => {
  const filled = rows.filter((row) => row.value != null && row.value !== "");
  if (!filled.length) return <p className={styles.empty}>{emptyText}</p>;

  return (
    <dl className={cs(styles.grid, className)}>
      {filled.map((row) => (
        <div className={styles.gridRow} key={row.label}>
          <dt className={styles.gridLabel}>{row.label}</dt>
          {/* Long values (release notes) stay on one line; the full text is
              in the tooltip. */}
          <dd
            className={styles.gridValue}
            title={typeof row.value === "string" ? row.value : undefined}
          >
            {row.value}
          </dd>
        </div>
      ))}
    </dl>
  );
};

/** Horizontal screenshot strip, used by the Screenshots section. */
export const ScreenshotStrip: React.FC<{
  urls?: string[];
  emptyText?: string;
  className?: string;
}> = ({ urls = [], emptyText = "No data yet", className }) => {
  if (!urls.length) return <p className={styles.empty}>{emptyText}</p>;

  return (
    <div className={cs(styles.strip, className)}>
      {urls.map((url) => (
        <img
          key={url}
          className={styles.shot}
          src={url}
          alt=""
          loading="lazy"
        />
      ))}
    </div>
  );
};

export type ChartCurrent = {
  overall?: number | null;
  category?: number | null;
  genreName?: string | null;
};

export type ChartPoint = {
  position?: number | null;
  capturedAt?: string | null;
};

export type ChartHistoryProps = {
  /** Positions measured right now — shown even before any history exists. */
  current?: ChartCurrent | null;
  /** Storefront-wide chart. */
  overall?: ChartPoint[];
  /** The app's category chart. */
  category?: ChartPoint[];
  /** Explains an empty graph while the current positions are already shown. */
  historyEmptyText?: string;
  emptyText?: string;
  className?: string;
};

const CHART_W = 640;
const CHART_H = 160;

/**
 * Chart position over time. The Y axis is inverted — position 1 belongs at the
 * top — and the scale starts at the best position reached, so a run between
 * #40 and #60 does not collapse into a flat line at the bottom of a 1..200 axis.
 *
 * Points where the app was outside the chart are breaks in the line, not zeros:
 * drawing them at the bottom would read as "ranked last" instead of "not ranked".
 */
const buildPath = (points: ChartPoint[], min: number, max: number): string => {
  const span = Math.max(1, max - min);
  let path = "";
  let penDown = false;

  points.forEach((point, i) => {
    if (point.position == null) {
      penDown = false;
      return;
    }
    const x =
      points.length === 1 ? CHART_W / 2 : (i / (points.length - 1)) * CHART_W;
    const y = ((point.position - min) / span) * (CHART_H - 16) + 8;
    path += `${penDown ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)} `;
    penDown = true;
  });

  return path.trim();
};

export const ChartHistory: React.FC<ChartHistoryProps> = ({
  current,
  overall = [],
  category = [],
  historyEmptyText = "History starts building from the first snapshot",
  emptyText = "No data yet",
  className,
}) => {
  const ranked = [...overall, ...category]
    .map((point) => point.position)
    .filter((position): position is number => position != null);

  const hasCurrent =
    current != null && (current.overall != null || current.category != null);

  if (!ranked.length && !hasCurrent) {
    return <p className={styles.empty}>{emptyText}</p>;
  }

  const currentBlock = hasCurrent ? (
    <div className={styles.positions}>
      <div className={styles.position}>
        <span className={styles.positionLabel}>Overall chart</span>
        <span className={styles.positionValue}>
          {current?.overall != null ? `#${current.overall}` : "Outside top 100"}
        </span>
      </div>
      <div className={styles.position}>
        <span className={styles.positionLabel}>
          {current?.genreName ? `${current.genreName} chart` : "Category chart"}
        </span>
        <span className={styles.positionValue}>
          {current?.category != null
            ? `#${current.category}`
            : "Outside top 100"}
        </span>
      </div>
    </div>
  ) : null;

  if (!ranked.length) {
    return (
      <div className={cs(styles.chart, className)}>
        {currentBlock}
        <p className={styles.empty}>{historyEmptyText}</p>
      </div>
    );
  }

  const min = Math.min(...ranked);
  const max = Math.max(...ranked);

  return (
    <div className={cs(styles.chart, className)}>
      {currentBlock}
      <svg
        className={styles.chartSvg}
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Chart position history"
      >
        {category.length > 0 && (
          <path
            className={styles.chartLineCategory}
            d={buildPath(category, min, max)}
            fill="none"
          />
        )}
        {overall.length > 0 && (
          <path
            className={styles.chartLineOverall}
            d={buildPath(overall, min, max)}
            fill="none"
          />
        )}
      </svg>

      <div className={styles.chartLegend}>
        <span className={styles.chartScale}>
          Best #{min} · worst #{max}
        </span>
        <span className={styles.chartKey}>
          <span className={cs(styles.chartDot, styles.chartDotOverall)} />
          Overall
        </span>
        <span className={styles.chartKey}>
          <span className={cs(styles.chartDot, styles.chartDotCategory)} />
          Category
        </span>
      </div>
    </div>
  );
};

export type IconPaletteColor = { hex: string; share: number };

export type IconPaletteItem = {
  appId: string;
  title: string;
  icon?: string | null;
  colors: IconPaletteColor[];
};

export type IconPaletteProps = {
  /** Own app first, competitors after — the order is the host's choice. */
  items: IconPaletteItem[];
  emptyText?: string;
  className?: string;
};

/**
 * Icon colour comparison: the dominant colours of the app's own icon next to
 * its competitors', one row of swatches per app.
 *
 * Colour is what makes an icon findable in a wall of search results, so the
 * useful question is not "is my icon nice" but "does it read as one of these,
 * or does it stand apart".
 */
export const IconPalette: React.FC<IconPaletteProps> = ({
  items,
  emptyText = "No data yet",
  className,
}) => {
  if (!items.length) return <p className={styles.empty}>{emptyText}</p>;

  return (
    <div className={cs(styles.palettes, className)}>
      {items.map((item) => (
        <div className={styles.paletteRow} key={item.appId}>
          <span className={styles.paletteApp}>
            {item.icon ? (
              <img className={styles.paletteIcon} src={item.icon} alt="" />
            ) : (
              <span className={styles.paletteIcon} />
            )}
            <span className={styles.paletteTitle}>{item.title}</span>
          </span>
          <span className={styles.swatches}>
            {item.colors.map((color) => (
              <span
                key={color.hex}
                className={styles.swatch}
                style={{ background: color.hex }}
                title={`${color.hex} — ${color.share}%`}
              />
            ))}
          </span>
        </div>
      ))}
    </div>
  );
};

export type ComparisonApp = {
  appId: string;
  title: string;
  icon?: string | null;
  /** The user's own app — the first column, the one the rest are read against. */
  own?: boolean;
};

export type ComparisonRow = {
  label: string;
  /** One entry per app, in the same order as `apps`. */
  values: React.ReactNode[];
};

export type ComparisonTableProps = {
  apps: ComparisonApp[];
  rows: ComparisonRow[];
  emptyText?: string;
  className?: string;
};

/**
 * Own app against its competitors, one column per app under its icon.
 *
 * A metadata field only means something next to the field the competition is
 * running: "27 characters" is neither good nor bad until you see that everyone
 * else uses 30. Scrolls horizontally rather than squeezing columns — five
 * columns of unreadable text compare nothing.
 */
export const ComparisonTable: React.FC<ComparisonTableProps> = ({
  apps,
  rows,
  emptyText = "No data yet",
  className,
}) => {
  if (!apps.length) return <p className={styles.empty}>{emptyText}</p>;

  // Every row is its own grid on the same template, so the rows can carry the
  // dividers and the columns still line up.
  const template = {
    gridTemplateColumns: `minmax(120px, 240px) repeat(${apps.length}, minmax(96px, 122px))`,
  };

  return (
    <div className={cs(styles.comparisonScroll, className)}>
      <div className={styles.comparison} role="table">
        <div
          className={cs(styles.comparisonRow, styles.comparisonHead)}
          style={template}
          role="row"
        >
          <span role="columnheader" />
          {apps.map((app) => (
            <span key={app.appId} role="columnheader" title={app.title}>
              {app.icon ? (
                <img className={styles.comparisonIcon} src={app.icon} alt="" />
              ) : (
                <span className={styles.comparisonIcon} />
              )}
            </span>
          ))}
        </div>
        {rows.map((row) => (
          <div
            className={styles.comparisonRow}
            style={template}
            role="row"
            key={row.label}
          >
            <span className={styles.comparisonLabel} role="rowheader">
              {row.label}
            </span>
            {apps.map((app, i) => (
              <span
                className={styles.comparisonCell}
                role="cell"
                key={app.appId}
              >
                {row.values[i] ?? "-"}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export type KeywordsTrendPoint = {
  date: string;
  top1?: number | null;
  top2to5?: number | null;
  top6to10?: number | null;
  top11to20?: number | null;
  top21to100?: number | null;
  top100plus?: number | null;
  total?: number | null;
};

export type KeywordsTrendProps = {
  points: KeywordsTrendPoint[];
  emptyText?: string;
  className?: string;
};

type TrendKey = keyof Omit<KeywordsTrendPoint, "date">;

// Colours of the Figma legend; the bands run warm-to-cool by depth.
const TREND_SERIES: Array<{ key: TrendKey; label: string; color: string }> = [
  {
    key: "total",
    label: "Total",
    color: "var(--button-brand-primary-bg-default, #4858a8)",
  },
  { key: "top1", label: "Top 1", color: "#b32e2f" },
  { key: "top2to5", label: "Top 2–5", color: "#f9c786" },
  { key: "top6to10", label: "Top 6–10", color: "#00bf7e" },
  {
    key: "top11to20",
    label: "Top 11–20",
    color: "var(--status-01-foreground-strong, #31adff)",
  },
  {
    key: "top21to100",
    label: "Top 21–100",
    color: "var(--status-03-foreground-strong, #875bf7)",
  },
  {
    key: "top100plus",
    label: "Top 100+",
    color: "var(--status-05-background-strong, #16bcdd)",
  },
];

const TREND_TICK = {
  fill: "var(--ui-text-quaternary-default)",
  fontFamily: "var(--ds-font-body)",
  fontSize: 14,
};

const formatTrendDate = (date: string) => {
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return `${String(parsed.getUTCDate()).padStart(2, "0")}.${String(
    parsed.getUTCMonth() + 1,
  ).padStart(2, "0")}`;
};

/** Legend entry: coloured dot and band name, then its value. */
const TrendKey: React.FC<{
  series: (typeof TREND_SERIES)[number];
  value: number;
}> = ({ series, value }) => (
  <span className={styles.trendKey}>
    <span className={styles.trendName}>
      <span className={styles.trendDot} style={{ background: series.color }} />
      {series.label}
    </span>
    <span className={styles.trendLast}>{value}</span>
  </span>
);

const TrendTooltip: React.FC<{
  active?: boolean;
  label?: string;
  payload?: Array<{ dataKey?: string | number; value?: number }>;
}> = ({ active, label, payload }) => {
  if (!active || !payload?.length || !label) return null;
  const values = new Map(payload.map((item) => [item.dataKey, item.value]));

  return (
    <div className={styles.trendTooltip}>
      <span className={styles.trendTooltipDate}>{formatTrendDate(label)}</span>
      {TREND_SERIES.map((series) => (
        <TrendKey
          key={series.key}
          series={series}
          value={values.get(series.key) ?? 0}
        />
      ))}
    </div>
  );
};

/**
 * How the keyword distribution moved day by day — drawn like the App Insights
 * "Track keyword" chart, with the latest value of each band in the legend.
 *
 * A single snapshot says where the app stands; only the trend says whether the
 * work is paying off. All series share one axis from zero, so the bands stay
 * comparable to each other and to the total.
 */
export const KeywordsTrend: React.FC<KeywordsTrendProps> = ({
  points,
  emptyText = "No data yet",
  className,
}) => {
  if (points.length < 2) return <p className={styles.empty}>{emptyText}</p>;

  const last = points[points.length - 1];

  return (
    <div className={cs(styles.trend, className)}>
      <div className={styles.trendChart}>
        <ResponsiveContainer>
          <LineChart
            data={points}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke="var(--ui-border-secondary-default)" />
            <XAxis
              dataKey="date"
              tickFormatter={formatTrendDate}
              tick={TREND_TICK}
              tickLine={false}
              axisLine={{ stroke: "var(--ui-border-secondary-default)" }}
              tickMargin={12}
              minTickGap={24}
            />
            <YAxis
              allowDecimals={false}
              tick={TREND_TICK}
              tickLine={false}
              axisLine={{ stroke: "var(--ui-border-secondary-default)" }}
              width={32}
            />
            <Tooltip
              content={<TrendTooltip />}
              cursor={{ stroke: "var(--button-brand-primary-bg-default)" }}
              isAnimationActive={false}
            />
            {TREND_SERIES.map((series) => (
              <Line
                key={series.key}
                type="linear"
                dataKey={series.key}
                stroke={series.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, fill: "white" }}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className={styles.trendLegend}>
        {TREND_SERIES.map((series) => (
          <TrendKey
            key={series.key}
            series={series}
            value={last[series.key] ?? 0}
          />
        ))}
      </div>
    </div>
  );
};

export type VersionRelease = {
  version: string;
  /** Display-ready date. */
  date?: string | null;
  notes?: string | null;
};

export type VersionTimelineProps = {
  /** Newest first — the first release gets the "Latest" pill. */
  releases: VersionRelease[];
  /** Caption above the list (Figma: "Releases listed"). */
  label?: string;
  emptyText?: string;
  className?: string;
};

/**
 * Release history, newest first. Update cadence is an ASO signal in itself —
 * a store listing that has not moved in a year reads differently from one
 * shipping every fortnight — so the dates carry as much weight as the notes.
 */
export const VersionTimeline: React.FC<VersionTimelineProps> = ({
  releases,
  label,
  emptyText = "No data yet",
  className,
}) => {
  if (!releases.length) return <p className={styles.empty}>{emptyText}</p>;

  return (
    <div className={cs(styles.timeline, className)}>
      {label && <span className={styles.timelineLabel}>{label}</span>}
      <ol className={styles.releases}>
        {releases.map((release, i) => (
          <li
            className={cs(styles.release, { [styles.releaseLatest]: i === 0 })}
            key={release.version}
          >
            <span className={styles.releaseHead}>
              <span className={styles.releaseVersion}>{release.version}</span>
              {release.date && (
                <span className={styles.releaseDate}>{release.date}</span>
              )}
              {i === 0 && <span className={styles.releaseBadge}>Latest</span>}
            </span>
            {release.notes && (
              <p className={styles.releaseNotes} title={release.notes}>
                {release.notes}
              </p>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
};
